import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Item Scoring|Modal|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Exam,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsExamsItemScoringModal';
export type SPCatalogsExamsItemScoringModalLocNames =
	| `${LocNameStart}TitleEditDialogTitle`
	| `${LocNameStart}TitleCreateDialogTitle`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}ErrorNameRequired`
	| `${LocNameStart}ErrorNameUnique`
	| `${LocNameStart}LabelLanguage`
	| `${LocNameStart}ErrorLanguageRequired`
	| `${LocNameStart}ErrorLanguageInvalid`
	| `${LocNameStart}LabelWeight`
	| `${LocNameStart}ErrorWeightInvalid`
	| `${LocNameStart}LabelMaxScore`
	| `${LocNameStart}ErrorMaxScoreInvalid`
	| `${LocNameStart}LabelCorrectAnswer`
	| `${LocNameStart}LabelUnscoredForms`
	| `${LocNameStart}ButtonDeleteUnscoredForm`
	| `${LocNameStart}ButtonAddUnscoredForm`
	| `${LocNameStart}ErrorUnscoredFormsDuplicate`
	| `${LocNameStart}LabelComment`
	| `${LocNameStart}ButtonCancelEdit`
	| `${LocNameStart}ButtonEditItem`
	| `${LocNameStart}ButtonCancelAdd`
	| `${LocNameStart}ButtonAddItem`;

export const spCatalogsExamsItemScoringModalLocs: LocDefs<SPCatalogsExamsItemScoringModalLocNames> =
	{
		SPCatalogsExamsItemScoringModalTitleEditDialogTitle: {
			defaultValue: 'Edit Exam Item',
			description: 'Edit Title',
			displayPath: `${pathStart}Edit Title`,
			displayType: 'Title',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Title.EditDialogTitle',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalTitleCreateDialogTitle: {
			defaultValue: 'Add Exam Item',
			description: 'Add Title',
			displayPath: `${pathStart}Add Title`,
			displayType: 'Title',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Title.CreateDialogTitle',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalLabelName: {
			defaultValue: 'Name',
			description: 'Field label for: Name.',
			displayPath: `${pathStart}Name`,
			displayType: 'Label',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.Name',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalErrorNameRequired: {
			defaultValue: 'Item name is required',
			description: 'Error when item name is required.',
			displayPath: `${pathStart}Name Required`,
			displayType: 'Error',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.NameRequired',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalErrorNameUnique: {
			defaultValue: 'Item name already exists',
			description: 'Error when item name already exists.',
			displayPath: `${pathStart}Name exists`,
			displayType: 'Error',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.NameUnique',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalLabelLanguage: {
			defaultValue: 'Language',
			description: 'Field label for: Language.',
			displayPath: `${pathStart}Language`,
			displayType: 'Label',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.Language',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalErrorLanguageRequired: {
			defaultValue: 'Language is required',
			description: 'Error when language is required.',
			displayPath: `${pathStart}Language required`,
			displayType: 'Error',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.LanguageRequired',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalErrorLanguageInvalid: {
			defaultValue: 'Invalid language selection',
			description: 'Error when language is invalid.',
			displayPath: `${pathStart}Language invalid`,
			displayType: 'Error',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.LanguageInvalid',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalLabelWeight: {
			defaultValue: 'Weight',
			description: 'Field label for: Weight.',
			displayPath: `${pathStart}Weight`,
			displayType: 'Label',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.Weight',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalErrorWeightInvalid: {
			defaultValue: 'Must be a number',
			description: 'Field error saying weight must be a number',
			displayPath: `${pathStart}Weight invlid`,
			displayType: 'Error',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.WeightInvalid',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalLabelMaxScore: {
			defaultValue: 'Max Score',
			description: 'Field label for: Max Score.',
			displayPath: `${pathStart}Max Score`,
			displayType: 'Label',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.Maximum',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalErrorMaxScoreInvalid: {
			defaultValue: 'Must be a number',
			description: 'Field error saying max score must be a number',
			displayPath: `${pathStart}Max score invlid`,
			displayType: 'Error',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.MaximumInvalid',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalLabelCorrectAnswer: {
			defaultValue: 'Correct Answer',
			description: 'Field label for: Correct Answer.',
			displayPath: `${pathStart}Correct Answer`,
			displayType: 'Label',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.CorrectAnswer',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalLabelUnscoredForms: {
			defaultValue: 'Unscored Forms',
			description: 'Field label for: Unscored Forms.',
			displayPath: `${pathStart}Unscored Forms`,
			displayType: 'Label',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.UnscoredForms',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalButtonDeleteUnscoredForm: {
			defaultValue: 'Delete Unscored Form',
			description: 'Delete Unscored Form accessiblity label.',
			displayPath: `${pathStart}Delete Unscored Form`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.DeleteUnscoredForm',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalButtonAddUnscoredForm: {
			defaultValue: 'Add Unscored Form',
			description: 'Add Unscored Form accessiblity label.',
			displayPath: `${pathStart}Add Unscored Form`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.AddUnscoredForm',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalErrorUnscoredFormsDuplicate: {
			defaultValue: 'Unscored forms must not contain duplicates',
			description: 'Error when unscored forms contain duplicates',
			displayPath: `${pathStart}Unscored Form Duplicate Error`,
			displayType: 'Error',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldError.UnscoredFormsDuplicate',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalLabelComment: {
			defaultValue: 'Comment',
			description: 'Field label for: Comment.',
			displayPath: `${pathStart}Comment`,
			displayType: 'Label',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.FieldLabel.Comment',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalButtonCancelEdit: {
			defaultValue: 'Cancel',
			description: 'Cancel edit button',
			displayPath: `${pathStart}Cancel edit`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.EditDialogCancel',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalButtonEditItem: {
			defaultValue: 'Edit Exam Item',
			description: 'Edit Exam Item button',
			displayPath: `${pathStart}Edit Exam Item`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.EditDialogSave',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalButtonCancelAdd: {
			defaultValue: 'Cancel',
			description: 'Cancel add button',
			displayPath: `${pathStart}Cancel add`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.CreateDialogCancel',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringModalButtonAddItem: {
			defaultValue: 'Add Exam Item',
			description: 'Add Exam Item button',
			displayPath: `${pathStart}Add Exam Item`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.CreateDialogSave',
			...sharedProperties,
		},
	};

export type SPCatalogsExamsItemScoringModalLocType =
	LocMap<SPCatalogsExamsItemScoringModalLocNames>;
